import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Link2, FileText, Image as ImageIcon, Loader2, ArrowLeft, Wand2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "url", label: "Link", icon: Link2 },
  { key: "text", label: "Testo", icon: FileText },
  { key: "screenshots", label: "Screenshot", icon: ImageIcon },
];

const toB64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

export default function ImportProperty() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("url");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const finish = (data) => { sessionStorage.setItem("sg_import", JSON.stringify({ data })); navigate("/dashboard/properties/review"); };

  const run = async () => {
    setLoading(true);
    try {
      if (tab === "url") {
        if (!url.trim()) return;
        const { data } = await api.post("/import/url", { url });
        if (!data.ok) { toast.warning(data.error || "Import automatico non riuscito — prova a incollare il testo"); setTab("text"); }
        else finish(data.data);
      } else if (tab === "text") {
        if (text.trim().length < 20) return toast.warning("Incolla più testo dell'annuncio");
        const { data } = await api.post("/import/text", { text });
        finish(data.data);
      } else {
        if (!images.length) return toast.warning("Aggiungi almeno uno screenshot");
        const b64 = await Promise.all(images.map(toB64));
        const { data } = await api.post("/import/screenshots", { images: b64 });
        finish(data.data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="px-4 pt-6 pb-10">
      <button onClick={() => navigate("/dashboard/properties/new")} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="w-4 h-4" /> Indietro</button>
      <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Importa la struttura</h1>
      <p className="mt-1 text-slate-500">Incolla un link, il testo dell'annuncio o carica degli screenshot. L'AI struttura tutto — potrai rivedere prima di salvare.</p>

      <div className="mt-5 flex gap-1.5 bg-slate-100 p-1 rounded-xl">
        {TABS.map((t) => <button key={t.key} onClick={() => setTab(t.key)} data-testid={`import-tab-${t.key}`} className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg ${tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}><t.icon className="w-4 h-4" /> {t.label}</button>)}
      </div>

      <div className="mt-5 rounded-2xl border border-slate-100 bg-white p-5">
        {tab === "url" && <><label className="text-sm font-semibold text-slate-700">Link dell'annuncio</label>
          <input data-testid="import-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.airbnb.it/rooms/..." className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" />
          <p className="text-xs text-slate-400 mt-2">Airbnb, Booking.com, Vrbo o il sito della tua struttura.</p></>}
        {tab === "text" && <><label className="text-sm font-semibold text-slate-700">Incolla le informazioni dell'annuncio</label>
          <textarea data-testid="import-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder="Copia descrizione, servizi, regole… e incolla qui" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500 resize-none" /></>}
        {tab === "screenshots" && <><label className="text-sm font-semibold text-slate-700">Carica gli screenshot</label>
          <input data-testid="import-screenshots-input" type="file" accept="image/*" multiple onChange={(e) => setImages(Array.from(e.target.files))} className="mt-2 block w-full text-sm text-slate-500 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-blue-600 file:font-semibold" />
          {images.length > 0 && <p className="text-xs text-slate-500 mt-2">{images.length} immagine/i selezionate</p>}</>}
        <button data-testid={`import-${tab}-submit`} onClick={run} disabled={loading} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-blue-500 text-white font-semibold rounded-xl px-5 py-3 hover:bg-blue-600 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {tab === "url" ? "Importa struttura" : "Struttura con AI"}
        </button>
      </div>
    </div>
  );
}
